import React, {
  useState, useEffect, useCallback, useRef, useMemo, memo,
} from 'react';
import { getAvatarColor, getInitials } from '../../../utils/avatarUtils';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, Dropdown, Badge } from 'antd';
import {
  LoadingOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  ReloadOutlined,
  EllipsisOutlined,
  CaretDownFilled,
  CaretRightFilled,
  ExclamationCircleFilled,
  WarningFilled,
  InfoCircleFilled,
  CloseOutlined,
  StarOutlined,
  StarFilled,
  DeleteOutlined,
  FolderOpenOutlined,
  PrinterOutlined,
  FullscreenOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faReply, faReplyAll, faShare,
  faChevronDown, faChevronUp,
  faFlag, faEnvelopeOpen, faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import { fetchConversationMessages } from '../../../Slices/Thunk/graphMailThunks';
import { forwardEmailThunk } from '../../../Slices/Thunk/notification';
import { outlookReplyThunk } from '../../../Slices/Thunk/outlookComposeThunks';
import dayjs from 'dayjs';
import isToday     from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';

import {
  selectMessagesByThread,
  selectMessageBodyStatus,
  selectLatestMessageInThread,
  fetchMessageBody,
  fetchAttachments,
  markMessageRead,
  unblockImages,
} from '../../../Slices/mailMessagesSlice';
import {
  selectSelectedThreadId,
  selectInlineComposeSessionId,
  setInlineComposeSession,
  setRibbonMode,
  clearInlineCompose,
  pushNotification,
} from '../../../Slices/mailUiSlice';
import {
  selectThreadById,
  markThreadRead,
  setThreadFlagged,
  deleteThread,
} from '../../../Slices/mailThreadsSlice';

dayjs.extend(isToday);
dayjs.extend(isYesterday);

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const C = {
  blue:        '#0f6cbd',
  blueDark:    '#115ea3',
  blueLight:   '#eff6fc',
  blueBg:      '#deecf9',
  text:        '#242424',
  textSecond:  '#605e5c',
  textMuted:   '#8a8886',
  border:      '#edebe9',
  borderLight: '#f3f2f1',
  bg:          '#fff',
  bgAlt:       '#faf9f8',
  red:         '#c50f1f',
  redBg:       '#fde7e9',
  orange:      '#ca5010',
  orangeBg:    '#fff4ce',
  green:       '#107c10',
  shadow:      '0 1px 4px rgba(0,0,0,0.08)',
};

const FONT   = "'Segoe UI', Tahoma, sans-serif";
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtFull(iso) {
  return iso ? dayjs(iso).format('ddd, MMM D, YYYY h:mm A') : '';
}
function fmtShort(iso) {
  if (!iso) return '';
  const d = dayjs(iso);
  if (d.isToday())                 return d.format('h:mm A');
  if (d.isYesterday())             return 'Yesterday ' + d.format('h:mm A');
  if (dayjs().diff(d,'day') < 7)  return d.format('ddd h:mm A');
  if (dayjs().diff(d,'year') < 1) return d.format('MMM D');
  return d.format('M/D/YY');
}
function fmtBytes(n) {
  if (!n) return '';
  if (n < 1024)        return `${n} B`;
  if (n < 1048576)     return `${(n/1024).toFixed(0)} KB`;
  return `${(n/1048576).toFixed(1)} MB`;
}
function recipientStr(arr = []) {
  return arr.map(r => r.name || r.email || r).join(', ');
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// EmailBody — renders email HTML in a scoped div (no iframe, so scroll works)
// ---------------------------------------------------------------------------

function sanitizeEmailHtml(html, imagesUnblocked) {
  if (!html) return '';
  let c = html;
  // Strip <script> tags
  c = c.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  // Strip <style> tags — they would pollute the page's global CSS and break layout/scroll
  c = c.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
  // Strip <link rel="stylesheet"> tags
  c = c.replace(/<link[^>]+rel=["']stylesheet["'][^>]*>/gi, '');
  // Strip outer <html>, <head>, <body> wrapper tags (content stays)
  c = c.replace(/<\/?(html|head|body)[^>]*>/gi, '');
  // Block external images if not unblocked
  if (!imagesUnblocked) {
    c = c.replace(
      /(<img[^>]+\s)src="(https?:\/\/[^"]+)"/gi,
      '$1src="" data-blocked-src="$2"',
    );
  }
  return c;
}

const EmailBody = memo(function EmailBody({ html, imagesUnblocked, messageId, onUnblock }) {
  const ref = useRef(null);

  const processed = useMemo(() => sanitizeEmailHtml(html, imagesUnblocked), [html, imagesUnblocked]);

  // Patch links to open in new tab
  useEffect(() => {
    if (!ref.current) return;
    ref.current.querySelectorAll('a[href]').forEach(a => {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    });
  }, [processed]);

  const blockedCount = useMemo(() => {
    if (imagesUnblocked || !html) return 0;
    return (html.match(/src="https?:\/\//gi) || []).length;
  }, [html, imagesUnblocked]);

  return (
    <>
      {blockedCount > 0 && !imagesUnblocked && (
        <div style={s.imgBanner}>
          <WarningFilled style={{ color: C.orange, fontSize: 14 }} />
          <span style={{ flex: 1, fontSize: 12, color: C.textSecond }}>
            Some pictures have been blocked to protect your privacy.
          </span>
          <button style={s.imgBannerBtn} onClick={() => onUnblock(messageId)}>
            Show pictures
          </button>
        </div>
      )}
      <div
        ref={ref}
        className="bpa-email-body"
        dangerouslySetInnerHTML={{ __html: processed }}
        style={{
          padding: '14px 18px',
          fontFamily: "'Segoe UI', Tahoma, sans-serif",
          fontSize: 14, color: '#242424', lineHeight: 1.6,
          wordBreak: 'break-word', overflowX: 'auto',
        }}
      />
    </>
  );
});

// ---------------------------------------------------------------------------
// SecurityBanner — renders one coloured alert strip
// ---------------------------------------------------------------------------

const SecurityBanner = memo(function SecurityBanner({ type, message: msg, onDismiss }) {
  const map = {
    phishing:  { bg:'#fde7e9', border:'#c50f1f', color:'#c50f1f', Icon: ExclamationCircleFilled },
    warning:   { bg:'#fff4ce', border:'#ca5010', color:'#ca5010', Icon: WarningFilled           },
    info:      { bg:'#eff6fc', border:'#0f6cbd', color:'#0f6cbd', Icon: InfoCircleFilled         },
  };
  const t = map[type] || map.info;

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10,
      padding:'8px 14px',
      background: t.bg,
      borderBottom: `1px solid ${t.border}`,
      fontFamily: FONT, fontSize: 12,
    }}>
      <t.Icon style={{ color: t.color, fontSize: 14, flexShrink: 0 }} />
      <span style={{ flex:1, color: C.text }}>{msg}</span>
      {onDismiss && type !== 'phishing' && (
        <button style={s.bannerClose} onClick={onDismiss} aria-label="Dismiss">
          <CloseOutlined style={{ fontSize: 10 }} />
        </button>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// AttachmentChip
// ---------------------------------------------------------------------------

const AttachmentChip = memo(function AttachmentChip({ att }) {
  function download() {
    if (!att.contentBytes) return;
    try {
      const raw  = atob(att.contentBytes);
      const arr  = Uint8Array.from(raw, c => c.charCodeAt(0));
      const blob = new Blob([arr], { type: att.contentType });
      const url  = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'),
        { href: url, download: att.name }).click();
      URL.revokeObjectURL(url);
    } catch { /* download failed — browser already shows no download */ }
  }

  return (
    <Tooltip title={`${att.name}${att.size ? ' · ' + fmtBytes(att.size) : ''}`}
             mouseEnterDelay={0.5}>
      <div style={s.attChip} onClick={download}
        role="button" tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && download()}>
        <PaperClipOutlined style={{ color: C.blue, fontSize: 12, flexShrink:0 }} />
        <span style={s.attName}>{att.name}</span>
        {att.size > 0 && <span style={s.attSize}>{fmtBytes(att.size)}</span>}
        <DownloadOutlined style={{ color: C.textMuted, fontSize: 11, flexShrink:0 }} />
      </div>
    </Tooltip>
  );
});

// ---------------------------------------------------------------------------
// ReplyCompose — full-pane Outlook-style reply/forward compose view
// Replaces the thread view when active. Structure:
//   [Action bar: Send | From | icons]
//   [To row]
//   [Blank body textarea — user types here]
//   [··· toggle]
//   [Quoted previous message — collapsed by default, READ-ONLY]
// ---------------------------------------------------------------------------

function ReplyCompose({ type, message, fromEmail, onClose, onPopOut }) {
  const dispatch   = useDispatch();
  const textRef    = useRef(null);

  const [body,           setBody]           = useState('');
  const [toValue,        setToValue]        = useState('');   // display label
  const [toEmails,       setToEmails]       = useState('');   // actual email(s) for sending
  const [sending,        setSending]        = useState(false);
  const [error,          setError]          = useState('');
  const [quotedExpanded, setQuotedExpanded] = useState(false);
  const [bodyLoading,    setBodyLoading]    = useState(false);

  // Pull the message body from Redux (may need loading on "..." click)
  const msgBodyContent = useSelector(s => s.mailMessages.byId[message.id]?.body?.content || '');
  const msgBodyLoaded  = useSelector(s => s.mailMessages.byId[message.id]?.body?.loaded  || false);

  // Pre-fill To field and focus
  useEffect(() => {
    if (type === 'reply') {
      // Display name but send email
      setToValue(message.from?.name || message.from?.email || '');
      setToEmails(message.from?.email || '');
    } else if (type === 'replyAll') {
      const addrs = [
        message.from?.email,
        ...(message.toRecipients || []).map(r => r.email),
      ].filter(e => e && e !== fromEmail);
      const unique = [...new Set(addrs)].join('; ');
      setToValue(unique);
      setToEmails(unique);
    }
    setTimeout(() => textRef.current?.focus(), 80);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subject = (type === 'forward' ? 'FW: ' : 'RE: ') +
    (message.subject || '').replace(/^(RE:|FW:|Fwd:)\s*/i, '');

  // "..." click — lazy-load body then toggle quoted block
  async function handleToggleQuoted() {
    if (!quotedExpanded && !msgBodyLoaded) {
      setBodyLoading(true);
      try { await dispatch(fetchMessageBody({ messageId: message.id })); }
      catch { /* show bodyPreview fallback */ }
      finally { setBodyLoading(false); }
    }
    setQuotedExpanded(p => !p);
  }

  // Build the quoted-original block for the optimistic body so "..." split works immediately.
  // Mirrors what Graph appends automatically to the actual sent email.
  function buildQuotedBlock() {
    const from = message.from?.name
      ? `${message.from.name} &lt;${message.from.email}&gt;`
      : (message.from?.email || '');
    const sent = (message.sentDateTime || message.receivedDateTime)
      ? dayjs(message.sentDateTime || message.receivedDateTime).format('dddd, MMMM D, YYYY h:mm A')
      : '';
    const to = (message.toRecipients || [])
      .map(r => r.name ? `${r.name} &lt;${r.email}&gt;` : r.email)
      .join('; ');
    const subj    = message.subject || '';
    const bodySrc = msgBodyContent || message.bodyPreview || '';

    return `<hr tabindex="-1" style="display:inline-block;width:98%">` +
      `<div id="divRplyFwdMsg" dir="ltr" style="margin-top:20px;padding-top:10px;` +
      `font-family:Calibri,sans-serif;font-size:11pt;color:#000000">` +
      `<p style="margin:0"><b>From:</b> ${from}</p>` +
      `<p style="margin:0"><b>Sent:</b> ${sent}</p>` +
      `<p style="margin:0"><b>To:</b> ${to}</p>` +
      `<p style="margin:0"><b>Subject:</b> ${subj}</p>` +
      `<br><div>${bodySrc}</div></div>`;
  }

  // Extract user-written content by removing quoted block
  function extractUserContent(fullHTML) {
    // Split on the quoted message marker (hr tag with specific attributes)
    const quoteMarker = '<hr tabindex="-1" style="display:inline-block;width:98%">';
    const parts = fullHTML.split(quoteMarker);
    
    // Return only the first part (user content) or full content if no quote marker
    return parts[0] || fullHTML;
  }

  async function handleSend() {
    const sendTo = type === 'forward' ? toValue : toEmails;
    if (!sendTo.trim()) { setError('Please add at least one recipient.'); return; }
    if (!body.trim())   { setError('Please write a message before sending.'); return; }
    setSending(true);
    setError('');
    try {
      const htmlBody = body.replace(/\n/g, '<br>');

      // Full body = user's text + quoted block (for editor display)
      const fullContent = type !== 'forward'
        ? `${htmlBody}${buildQuotedBlock()}`
        : htmlBody;

      // Extract only user content for API payload (Graph API adds original message automatically)
      const userContent = type !== 'forward'
        ? extractUserContent(fullContent)
        : htmlBody;

      if (type === 'forward') {
        await dispatch(forwardEmailThunk({
          messageId: message.id, from: fromEmail,
          to: sendTo, subject, body: htmlBody, attachments: [],
        }));
      } else {
        await dispatch(outlookReplyThunk({
          messageId: message.id,
          comment: userContent, // Only user content, no quoted block
          toRecipients: sendTo.split(/[;,]+/).map(e => e.trim()),
          fileList: [], // ReadingPane doesn't support attachments
        })).unwrap();
      }

      dispatch(pushNotification({ type: 'success', message: 'Message sent', autoDismissMs: 3000 }));
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  }

  // Quoted message metadata lines
  const quotedFrom    = message.from?.name
    ? `${message.from.name} <${message.from.email}>`
    : message.from?.email || '';
  const quotedSent    = (message.sentDateTime || message.receivedDateTime)
    ? dayjs(message.sentDateTime || message.receivedDateTime).format('dddd, MMMM D, YYYY h:mm A')
    : '';
  const quotedTo      = recipientStr(message.toRecipients);
  const quotedSubject = message.subject || '';

  return (
    <div style={s.rcRoot}>

      {/* ── Action bar: Send | From | icons ── */}
      <div style={s.rcActionBar}>
        {/* Send button + dropdown chevron */}
        <div style={{ display:'flex', gap:0, flexShrink:0 }}>
          <button
            style={{ ...s.rcSendBtn, ...(sending ? { opacity:.7, cursor:'not-allowed' } : {}) }}
            onClick={handleSend} disabled={sending} className="rc-send-btn"
          >
            {sending
              ? <LoadingOutlined spin style={{ fontSize:13 }} />
              : <SendOutlined    style={{ fontSize:13 }} />}
            <span style={{ marginLeft:6 }}>{sending ? 'Sending…' : 'Send'}</span>
          </button>
          <button style={s.rcSendChevron} title="More send options">
            <CaretDownFilled style={{ fontSize:10 }} />
          </button>
        </div>

        {fromEmail && (
          <span style={s.rcFromLabel}>
            From: <strong style={{ fontWeight:600 }}>{fromEmail}</strong>
          </span>
        )}

        <div style={{ flex:1 }} />

        <div style={{ display:'flex', gap:2 }}>
          <Tooltip title="Pop out to full compose">
            <button style={s.rcIconBtn} className="rc-icon-btn" onClick={onPopOut}>
              <FullscreenOutlined style={{ fontSize:13 }} />
            </button>
          </Tooltip>
          <Tooltip title="Discard">
            <button style={s.rcIconBtn} className="rc-icon-btn rc-icon-btn--discard" onClick={onClose}>
              <DeleteOutlined style={{ fontSize:13 }} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* ── Scrollable compose + quoted area ── */}
      <div style={s.rcScrollArea}>

        {/* To row */}
        <div style={s.rcToRow}>
          <span style={s.rcFieldLabel}>To</span>
          {type === 'forward' ? (
            <input
              style={s.rcToInput}
              value={toValue}
              onChange={e => { setToValue(e.target.value); setError(''); }}
              placeholder="Add recipients…"
              autoFocus
            />
          ) : (
            <span style={s.rcToValue}>{toValue || '—'}</span>
          )}
        </div>

        {/* Body — only the reply text, NO quoted content merged in */}
        <textarea
          ref={textRef}
          value={body}
          onChange={e => { setBody(e.target.value); setError(''); }}
          placeholder="Write your message…"
          style={s.rcTextarea}
        />

        {/* Error */}
        {error && (
          <div style={s.rcError}>
            <ExclamationCircleFilled style={{ marginRight:5 }} />{error}
          </div>
        )}

        {/* "··· " toggle — separates reply body from quoted original */}
        <div style={s.rcDotsRow}>
          <Tooltip title={quotedExpanded ? 'Hide previous message' : 'Show previous message'}>
            <button style={s.rcDotsBtn} onClick={handleToggleQuoted} className="rc-dots-btn">
              {bodyLoading
                ? <LoadingOutlined spin style={{ fontSize:11 }} />
                : <span style={{ letterSpacing:2, fontWeight:700 }}>···</span>}
            </button>
          </Tooltip>
        </div>

        {/* Quoted block — collapsed by default, READ-ONLY */}
        {quotedExpanded && (
          <div style={s.rcQuoted}>
            {/* Metadata: From / Sent / To / Subject */}
            <div style={s.rcQuotedMeta}>
              {[
                ['From',    quotedFrom],
                ['Sent',    quotedSent],
                ['To',      quotedTo],
                ['Subject', quotedSubject],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={s.rcQuotedRow}>
                  <span style={s.rcQuotedLabel}>{label}:</span>
                  <span style={s.rcQuotedValue}>{value}</span>
                </div>
              ))}
            </div>

            {/* Body — render via EmailBody (read-only sandboxed) */}
            <div style={{ borderTop:`1px solid ${C.borderLight}`, marginTop:8 }}>
              {msgBodyLoaded && msgBodyContent ? (
                <EmailBody
                  html={msgBodyContent}
                  imagesUnblocked={false}
                  messageId={`quoted-${message.id}`}
                  onUnblock={() => {}}
                />
              ) : (
                <div style={{ padding:'10px 16px', fontSize:13, color:C.textSecond, fontFamily:FONT, lineHeight:1.6 }}>
                  {message.bodyPreview || ''}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MessageCard — one message in the thread accordion
// ---------------------------------------------------------------------------

function MessageCard({ message, isExpanded, isLatest, onToggle, onReply, onReplyAll, onForward,
  onFlag, onDelete, isFlagged, moreMenuItems }) {
  const dispatch   = useDispatch();
  const bodyStatus = useSelector(s => selectMessageBodyStatus(s, message.id));
  const [detailOpen, setDetailOpen] = useState(false);
  const [quotedOpen, setQuotedOpen] = useState(false);
  const readTimer  = useRef(null);

  const senderName  = message.from?.name  || message.from?.email  || 'Unknown';
  const senderEmail = message.from?.email || '';
  const color       = getAvatarColor(senderName);
  const abbr        = getInitials(senderName);

  // Lazy fetch body + attachments when expanded
  useEffect(() => {
    if (!isExpanded) return;
    if (bodyStatus === 'not-loaded')
      dispatch(fetchMessageBody({ messageId: message.id }));
    if (message.hasAttachments && !message.attachmentsLoaded)
      dispatch(fetchAttachments({ messageId: message.id }));
  }, [isExpanded, bodyStatus, message.id,
      message.hasAttachments, message.attachmentsLoaded, dispatch]);

  // Auto-mark read 2 s after expanding
  useEffect(() => {
    if (isExpanded && !message.isRead) {
      readTimer.current = setTimeout(
        () => dispatch(markMessageRead({ messageId: message.id })), 2000,
      );
    }
    return () => clearTimeout(readTimer.current);
  }, [isExpanded, message.isRead, message.id, dispatch]);

  // Split reply body into user's new text vs quoted previous message.
  // Graph natively appends quoted content after <hr> + <div id="divRplyFwdMsg">.
  // Find the EARLIEST split point across all known quote markers.
  const { mainHtml, quotedHtml } = useMemo(() => {
    const body = message.body?.content || '';
    if (!body) return { mainHtml: '', quotedHtml: '' };

    const splitPatterns = [
      /<hr[\s/>]/i,                                            // Standard HR (Graph uses this)
      /<div[^>]+id=["']divRplyFwdMsg["']/i,                   // Graph quoted-message div
      /<blockquote[^>]+type=["']cite["']/i,                    // Standards blockquote
      /<div[^>]*class=["'][^"']*gmail_quote[^"']*["']/i,       // Gmail quote div
    ];

    let splitIdx = -1;
    for (const pat of splitPatterns) {
      const idx = body.search(pat);
      if (idx > 0 && (splitIdx === -1 || idx < splitIdx)) {
        splitIdx = idx;
      }
    }

    if (splitIdx > 0) {
      return { mainHtml: body.slice(0, splitIdx), quotedHtml: body.slice(splitIdx) };
    }

    return { mainHtml: body, quotedHtml: '' };
  }, [message.body?.content]);

  // Build banners for this message
  const banners = useMemo(() => {
    const list = [];
    if (message.threatAssessment === 'high')
      list.push({ id:'phishing', type:'phishing',
        msg:'Be careful — this message may be a phishing attempt.' });
    if (message.hasIRM)
      list.push({ id:'irm', type:'info',
        msg:`This message is protected: ${message.irmLabel || 'IRM policy applied'}.` });
    if (message.importance === 'high')
      list.push({ id:'importance', type:'warning',
        msg:'This message was sent with high importance.' });
    return list;
  }, [message.threatAssessment, message.hasIRM,
      message.irmLabel, message.importance]);

  const [dismissedBanners, setDismissedBanners] = useState([]);
  const visibleBanners = banners.filter(b => !dismissedBanners.includes(b.id));

  // ── COLLAPSED ─────────────────────────────────────────────────────────────
  if (!isExpanded) {
    const preview = (message.bodyPreview || '')
      .replace(/<[^>]+>/g, '').slice(0, 120);

    return (
      <>
        <style>{CSS_HOVER}</style>
        <div style={s.cardCollapsed} onClick={onToggle}
          role="button" tabIndex={0} aria-expanded="false"
          onKeyDown={e => (e.key==='Enter'||e.key===' ') && onToggle()}
          className="rp-collapsed">
          <div style={{ ...s.avatar, background: color }}>{abbr}</div>
          <div style={{ flex:1, minWidth:0, overflow:'hidden' }}>
            <span style={s.collSender}>{senderName}</span>
            <span style={s.collPreview}>{preview}…</span>
          </div>
          <span style={s.collTime}>{fmtShort(message.sentDateTime || message.receivedDateTime)}</span>
          {message.hasAttachments &&
            <PaperClipOutlined style={{ color:C.textMuted, fontSize:11, flexShrink:0 }} />}
          <CaretRightFilled style={{ color:C.textMuted, fontSize:11, flexShrink:0 }} />
        </div>
      </>
    );
  }

  // ── EXPANDED ──────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS_HOVER}</style>
      <div style={s.cardExpanded}>

        {/* Security + info banners */}
        {visibleBanners.map(b => (
          <SecurityBanner
            key={b.id} type={b.type} message={b.msg}
            onDismiss={b.type !== 'phishing'
              ? () => setDismissedBanners(p => [...p, b.id])
              : null}
          />
        ))}

        {/* ── Card header ── */}
        <div style={s.cardHeader}
          onClick={onToggle}
          className="rp-header-click">

          {/* Left: avatar + sender details */}
          <div style={{ display:'flex', gap:10, flex:1, minWidth:0, alignItems:'flex-start' }}>
            <div style={{ ...s.avatar, background:color, flexShrink:0 }}>{abbr}</div>

            <div style={{ flex:1, minWidth:0 }}>
              {/* Name + email */}
              <div style={{ display:'flex', alignItems:'baseline', gap:6, flexWrap:'wrap' }}>
                <span style={s.senderName}>{senderName}</span>
                {senderEmail && senderEmail !== senderName && (
                  <span style={s.senderEmail}>&lt;{senderEmail}&gt;</span>
                )}
              </div>

              {/* To line */}
              <div style={s.recipRow}>
                <span style={s.recipLabel}>To: </span>
                <span style={s.recipVal}>{recipientStr(message.toRecipients)}</span>
              </div>

              {/* Expanded detail */}
              {detailOpen && (
                <div style={{ marginTop:4, display:'flex', flexDirection:'column', gap:2 }}>
                  {message.ccRecipients?.length > 0 && (
                    <div style={s.recipRow}>
                      <span style={s.recipLabel}>Cc: </span>
                      <span style={s.recipVal}>{recipientStr(message.ccRecipients)}</span>
                    </div>
                  )}
                  {message.bccRecipients?.length > 0 && (
                    <div style={s.recipRow}>
                      <span style={s.recipLabel}>Bcc: </span>
                      <span style={s.recipVal}>{recipientStr(message.bccRecipients)}</span>
                    </div>
                  )}
                  <div style={s.recipRow}>
                    <span style={s.recipLabel}>Sent: </span>
                    <span style={s.recipVal}>{fmtFull(message.sentDateTime || message.receivedDateTime)}</span>
                  </div>
                  {message.importance && message.importance !== 'normal' && (
                    <div style={s.recipRow}>
                      <span style={s.recipLabel}>Importance: </span>
                      <span style={{ ...s.recipVal, color: message.importance==='high' ? C.red : C.textMuted,
                        textTransform:'capitalize' }}>
                        {message.importance}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Detail toggle */}
              <button style={s.detailToggle}
                onClick={e => { e.stopPropagation(); setDetailOpen(p => !p); }}>
                {detailOpen
                  ? <><CaretDownFilled  style={{ fontSize:9 }} /> Hide details</>
                  : <><CaretRightFilled style={{ fontSize:9 }} /> Show details</>
                }
              </button>
            </div>
          </div>

          {/* Right: timestamp + reply actions (all expanded messages) */}
          <div style={s.headerRight} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', gap:2 }}>
              <Tooltip title={fmtFull(message.receivedDateTime)} mouseEnterDelay={0.5}>
                <span style={{ ...s.expandedTime, marginRight:8 }}>{fmtShort(message.receivedDateTime)}</span>
              </Tooltip>

              <Tooltip title="Reply" mouseEnterDelay={0.4}>
                <button style={s.hdrIconBtn} className="rp-icon-btn" onClick={() => onReply(message)}>
                  <FontAwesomeIcon icon={faReply} style={{ color:C.blue, fontSize:13 }} />
                </button>
              </Tooltip>
              <Tooltip title="Reply all" mouseEnterDelay={0.4}>
                <button style={s.hdrIconBtn} className="rp-icon-btn" onClick={() => onReplyAll(message)}>
                  <FontAwesomeIcon icon={faReplyAll} style={{ color:C.blue, fontSize:13 }} />
                </button>
              </Tooltip>
              <Tooltip title="Forward" mouseEnterDelay={0.4}>
                <button style={s.hdrIconBtn} className="rp-icon-btn" onClick={() => onForward(message)}>
                  <FontAwesomeIcon icon={faShare} style={{ color:C.blue, fontSize:13 }} />
                </button>
              </Tooltip>
              <Tooltip title={isFlagged ? 'Remove flag' : 'Flag'} mouseEnterDelay={0.4}>
                <button style={s.hdrIconBtn} className="rp-icon-btn" onClick={onFlag}>
                  {isFlagged
                    ? <StarFilled  style={{ color:C.orange, fontSize:13 }} />
                    : <StarOutlined style={{ color:C.textMuted, fontSize:13 }} />}
                </button>
              </Tooltip>
              <Tooltip title="Delete" mouseEnterDelay={0.4}>
                <button style={s.hdrIconBtn} className="rp-icon-btn rp-icon-btn--danger" onClick={onDelete}>
                  <DeleteOutlined style={{ color:C.textMuted, fontSize:13 }} />
                </button>
              </Tooltip>
              {isLatest && (
                <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement="bottomRight">
                  <button style={s.hdrIconBtn} className="rp-icon-btn">
                    <EllipsisOutlined style={{ color:C.textMuted, fontSize:13 }} />
                  </button>
                </Dropdown>
              )}
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={s.bodyWrap}>
          {bodyStatus === 'loading' && (
            <div style={s.bodyCenter}>
              <LoadingOutlined spin style={{ color:C.blue, fontSize:22 }} />
            </div>
          )}

          {bodyStatus === 'error' && (
            <div style={s.bodyCenter}>
              <span style={{ color:C.red, fontSize:13 }}>Failed to load message.</span>
              <button style={s.retryBtn}
                onClick={() => dispatch(fetchMessageBody({ messageId:message.id }))}>
                <ReloadOutlined /> Retry
              </button>
            </div>
          )}

          {bodyStatus === 'loaded' && (
            <>
              <EmailBody
                html={mainHtml}
                imagesUnblocked={message.body?.imagesUnblocked}
                messageId={message.id}
                onUnblock={mid => dispatch(unblockImages({ messageId:mid }))}
              />

              {quotedHtml && (
                <div style={{ padding:'0 16px 6px' }}>
                  <Tooltip title={quotedOpen ? 'Hide history' : 'Show history'}>
                    <button style={s.quotedBtn}
                      onClick={() => setQuotedOpen(p => !p)}>
                      <EllipsisOutlined />
                    </button>
                  </Tooltip>
                </div>
              )}

              {quotedOpen && quotedHtml && (
                <div style={{ borderTop:`1px solid ${C.borderLight}`,
                  background: C.bgAlt }}>
                  <EmailBody
                    html={quotedHtml}
                    imagesUnblocked={message.body?.imagesUnblocked}
                    messageId={`${message.id}-q`}
                    onUnblock={mid => dispatch(unblockImages({ messageId:mid }))}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Attachments ── */}
        {message.attachmentsLoaded && message.attachments?.filter(a=>!a.isInline).length > 0 && (
          <div style={s.attSection}>
            <span style={s.attLabel}>
              <PaperClipOutlined style={{ marginRight:5 }} />
              {message.attachments.filter(a=>!a.isInline).length} Attachment
              {message.attachments.filter(a=>!a.isInline).length > 1 ? 's' : ''}
            </span>
            <div style={s.attGrid}>
              {message.attachments.filter(a=>!a.isInline).map(a =>
                <AttachmentChip key={a.id} att={a} />
              )}
            </div>
          </div>
        )}

        {/* ── Bottom action buttons (Reply / Forward) ── */}
        {bodyStatus === 'loaded' && (
          <div style={s.cardActions}>
            <button style={s.cardActionBtn} className="rp-card-action-btn"
              onClick={e => { e.stopPropagation(); onReply(message); }}>
              <FontAwesomeIcon icon={faReply} style={{ fontSize:12 }} />
              <span>Reply</span>
            </button>
            <button style={s.cardActionBtn} className="rp-card-action-btn"
              onClick={e => { e.stopPropagation(); onForward(message); }}>
              <FontAwesomeIcon icon={faShare} style={{ fontSize:12 }} />
              <span>Forward</span>
            </button>
          </div>
        )}

      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// ReadingPane — root
// ---------------------------------------------------------------------------

export default function ReadingPane({ style, onOpenCompose, userEmail = '' }) {
  const dispatch       = useDispatch();
  const conversationId = useSelector(selectSelectedThreadId);
  const thread         = useSelector(s => selectThreadById(s, conversationId));
  const messages       = useSelector(s => selectMessagesByThread(s, conversationId));
  // Inline reply card state: null | { type: 'reply'|'replyAll'|'forward', message }
  const [inlineReply, setInlineReply] = useState(null);

  // ── Accordion state for older messages (accordion: one open at a time) ────────
  // Latest message (messages[0]) is always expanded — no state needed for it.
  // Older messages (messages.slice(1)) use this local Set.
  const [expandedOlderIds, setExpandedOlderIds] = useState(new Set());

  // Reset accordion and inline reply when conversation changes
  useEffect(() => {
    setInlineReply(null);
    setExpandedOlderIds(new Set());
    if (!conversationId) return;
    dispatch(fetchConversationMessages({ conversationId }));
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle older message — accordion: clicking open collapses it; clicking closed
  // collapses any currently open one and opens the new one (one at a time).
  const handleOlderToggle = useCallback((msgId) => {
    setExpandedOlderIds(prev => {
      if (prev.has(msgId)) return new Set();           // collapse if already open
      return new Set([msgId]);                         // accordion: open only this one
    });
  }, []);

  const handleExpandAll = useCallback(() =>
    setExpandedOlderIds(new Set(messages.slice(1).map(m => m.id))),
    [messages]);

  const handleCollapseAll = useCallback(() =>
    setExpandedOlderIds(new Set()), []);

  const triggerCompose = useCallback((type, message) => {
    if (typeof onOpenCompose === 'function') {
      onOpenCompose({ type, message, conversationId });
      return;
    }
    const sessionId = `session_${Date.now()}`;
    dispatch(setInlineComposeSession(sessionId));
    dispatch(setRibbonMode('compose'));
  }, [dispatch, conversationId, onOpenCompose]);

  // Open inline reply card at bottom of thread
  const handleReply    = useCallback(m => setInlineReply({ type: 'reply',    message: m }), []);
  const handleReplyAll = useCallback(m => setInlineReply({ type: 'replyAll', message: m }), []);
  const handleForward  = useCallback(m => {
    // Ensure body is in Redux before compose opens so buildInitialValues has content.
    if (m?.bodyStatus === 'not-loaded' || !m?.body?.content) {
      dispatch(fetchMessageBody({ messageId: m.id }));
    }
    triggerCompose('forward', m);
  }, [dispatch, triggerCompose]);

  // Pop out: close inline card, open full ComposeMail
  const handlePopOut = useCallback(() => {
    if (!inlineReply) return;
    const { type, message } = inlineReply;
    setInlineReply(null);
    triggerCompose(type, message);
  }, [inlineReply, triggerCompose]);

  const handleDelete = useCallback(() => {
    if (!thread) return;
    dispatch(deleteThread({ conversationId, folderId: thread.folderId }));
    dispatch(pushNotification({ type:'success', message:'Message deleted', autoDismissMs:3000 }));
  }, [dispatch, conversationId, thread]);

  const handleFlagToggle = useCallback(() => {
    if (!thread) return;
    dispatch(setThreadFlagged({ conversationId, isFlagged: !thread.isFlagged }));
  }, [dispatch, conversationId, thread]);

  const handleMarkRead = useCallback(() => {
    if (!thread) return;
    dispatch(markThreadRead({ conversationId }));
    messages.forEach(m => dispatch(markMessageRead({ messageId: m.id })));
  }, [dispatch, conversationId, thread, messages]);

  const handlePrint = useCallback(() => {
    const latest = messages[0]; // messages[0] is newest (descending sort)
    if (!latest) return;

    const from    = latest.from?.name || latest.from?.email || '';
    const to      = (latest.toRecipients || []).map(r => r.name || r.email).join(', ');
    const subject = thread?.subject || '(No subject)';
    const date    = latest.receivedDateTime
      ? dayjs(latest.receivedDateTime).format('ddd, MMM D, YYYY h:mm A')
      : '';
    const body    = latest.body?.content || latest.bodyPreview || '';

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${subject}</title>
  <style>
    body { font-family: 'Segoe UI', Calibri, Arial, sans-serif; font-size: 14px; color: #242424; margin: 32px; }
    .header { border-bottom: 1px solid #edebe9; padding-bottom: 16px; margin-bottom: 16px; }
    .subject { font-size: 20px; font-weight: 600; margin: 0 0 12px; }
    .meta { font-size: 13px; color: #444; line-height: 1.8; }
    .meta b { color: #242424; }
    .body { margin-top: 20px; line-height: 1.6; }
  </style>
</head>
<body>
  <div class="header">
    <div class="subject">${subject}</div>
    <div class="meta">
      <div><b>From:</b> ${from}</div>
      <div><b>To:</b> ${to}</div>
      <div><b>Date:</b> ${date}</div>
    </div>
  </div>
  <div class="body">${body}</div>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    // Wait for content (including images) to load before printing
    win.onload = () => { win.print(); win.close(); };
    // Fallback in case onload already fired
    setTimeout(() => { try { win.print(); win.close(); } catch(e) {} }, 800);
  }, [messages, thread]);

  const moreMenuItems = useMemo(() => [
    { key:'markRead',   label: thread?.hasUnread ? 'Mark as read'   : 'Mark as unread',
      icon: <FontAwesomeIcon icon={thread?.hasUnread ? faEnvelopeOpen : faEnvelope} />,
      onClick: handleMarkRead },
    { key:'flag',       label: thread?.isFlagged ? 'Remove flag' : 'Add flag',
      icon: <FontAwesomeIcon icon={faFlag} style={{ color: thread?.isFlagged ? C.orange : undefined }} />,
      onClick: handleFlagToggle },
    { type:'divider' },
    { key:'print',      label:'Print',   icon:<PrinterOutlined />,
      onClick: handlePrint },
  ], [thread, handleMarkRead, handleFlagToggle, handlePrint]);

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!conversationId || !thread) {
    return (
      <div style={{ ...s.root, alignItems:'center', justifyContent:'center',
        background:C.bgAlt, ...style }}>
        <div style={{ textAlign:'center', opacity:0.5 }}>
          <div style={{ fontSize:48, lineHeight:1 }}>✉️</div>
          <div style={{ fontSize:14, color:C.textSecond, marginTop:12, fontFamily:FONT }}>
            Select a message to read
          </div>
        </div>
      </div>
    );
  }

  // allExpanded = all OLDER messages (messages[1..n]) are open
  const allExpanded = messages.length > 1 &&
    messages.slice(1).every(m => expandedOlderIds.has(m.id));

  return (
    <>
      <style>{CSS_HOVER}</style>
      <div style={{ ...s.root, ...style }}>

        {inlineReply ? (
          /* ── Reply/Forward compose view replaces thread while active ── */
          <ReplyCompose
            type={inlineReply.type}
            message={inlineReply.message}
            fromEmail={userEmail || localStorage.getItem('outlookUserEmail') || ''}
            onClose={() => setInlineReply(null)}
            onPopOut={handlePopOut}
          />
        ) : (
          <>
            {/* ── Thread subject + meta ── */}
            <div style={s.subjectBar}>
              <h2 style={s.subject}>
                {thread.isFlagged &&
                  <StarFilled style={{ color:C.orange, fontSize:16, marginRight:8 }} />}
                {thread.subject || '(No subject)'}
              </h2>

              <div style={s.subjectMeta}>
                <span style={s.msgCount}>
                  {messages.length} message{messages.length !== 1 ? 's' : ''}
                </span>

                {messages.length > 1 && (
                  <button style={s.expandAllBtn}
                    onClick={allExpanded ? handleCollapseAll : handleExpandAll}
                    className="rp-expand-all">
                    <FontAwesomeIcon icon={allExpanded ? faChevronUp : faChevronDown}
                      style={{ fontSize:10 }} />
                    {allExpanded ? ' Collapse all' : ' Expand all'}
                  </button>
                )}

                {thread.categories?.length > 0 && thread.categories.map(cat => (
                  <span key={cat} style={s.categoryTag}>{cat}</span>
                ))}
              </div>
            </div>

            {/* ── Thread message structure ── */}
            {/* messages[0] = latest (newest), messages.slice(1) = older (newest→oldest) */}
            <div style={s.stack}>

              {/* ── LATEST MESSAGE — always expanded, never collapses ── */}
              {messages.length > 0 && (
                <MessageCard
                  key={messages[0].id}
                  message={messages[0]}
                  isExpanded={true}
                  isLatest={true}
                  onToggle={null}
                  onReply={handleReply}
                  onReplyAll={handleReplyAll}
                  onForward={handleForward}
                  onFlag={handleFlagToggle}
                  onDelete={handleDelete}
                  isFlagged={thread.isFlagged}
                  moreMenuItems={moreMenuItems}
                />
              )}

              {/* ── OLDER MESSAGES — compact rows, accordion (one open at a time) ── */}
              {messages.slice(1).map((msg) => (
                <MessageCard
                  key={msg.id}
                  message={msg}
                  isExpanded={expandedOlderIds.has(msg.id)}
                  isLatest={false}
                  onToggle={() => handleOlderToggle(msg.id)}
                  onReply={handleReply}
                  onReplyAll={handleReplyAll}
                  onForward={handleForward}
                  onFlag={handleFlagToggle}
                  onDelete={handleDelete}
                  isFlagged={thread.isFlagged}
                  moreMenuItems={moreMenuItems}
                />
              ))}

            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s = {
  root: {
    display:'flex', flexDirection:'column', flex:1, minHeight:0,
    background:C.bg, fontFamily:FONT, overflowY:'auto', overflowX:'hidden',
  },

  // Header icon button (reply/flag/delete/more in card header)
  hdrIconBtn: {
    display:'flex', alignItems:'center', justifyContent:'center',
    width:28, height:28, border:'none', borderRadius:4,
    background:'transparent', cursor:'pointer', transition:'background .1s',
    fontSize:14,
  },

  // Subject bar
  subjectBar: {
    padding:'12px 18px 10px',
    borderBottom:`1px solid ${C.border}`, flexShrink:0, background:C.bg,
  },
  subject: {
    margin:0, fontSize:20, fontWeight:600, color:C.text,
    lineHeight:1.3, fontFamily:FONT,
  },
  subjectMeta: {
    display:'flex', alignItems:'center', flexWrap:'wrap', gap:8, marginTop:6,
  },
  msgCount: { fontSize:12, color:C.textMuted },
  expandAllBtn: {
    display:'flex', alignItems:'center', gap:4,
    background:'none', border:'none', color:C.blue, fontSize:12,
    cursor:'pointer', padding:'2px 4px', borderRadius:3, fontFamily:FONT,
  },
  categoryTag: {
    padding:'1px 8px', borderRadius:10,
    background:C.blueBg, color:C.blue, fontSize:11,
  },

  // Stack scroll area — root scrolls, so no overflow needed here
  stack: {
    padding:'8px 12px 32px',
    display:'flex', flexDirection:'column', gap:4,
  },

  // Avatar
  avatar: {
    width:36, height:36, borderRadius:'50%', color:'#fff',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:13, fontWeight:600, letterSpacing:.3,
  },

  // Collapsed card
  cardCollapsed: {
    display:'flex', alignItems:'center', gap:8,
    padding:'8px 12px', borderRadius:4, border:`1px solid ${C.border}`,
    background:C.bgAlt, cursor:'pointer', userSelect:'none',
    fontFamily:FONT, boxSizing:'border-box', transition:'background .1s',
  },
  collSender: { fontSize:13, fontWeight:600, color:C.text, marginRight:8 },
  collPreview:{ fontSize:12, color:C.textMuted, overflow:'hidden',
    textOverflow:'ellipsis', whiteSpace:'nowrap' },
  collTime:   { fontSize:11, color:C.textMuted, whiteSpace:'nowrap',
    marginLeft:'auto', flexShrink:0 },

  // Expanded card
  cardExpanded: {
    borderRadius:4, border:`1px solid ${C.border}`,
    background:C.bg, boxShadow:C.shadow, overflow:'hidden', fontFamily:FONT,
  },
  cardHeader: {
    display:'flex', alignItems:'flex-start', justifyContent:'space-between',
    gap:12, padding:'12px 14px', borderBottom:`1px solid ${C.borderLight}`,
  },
  senderName:  { fontSize:14, fontWeight:700, color:C.text },
  senderEmail: { fontSize:12, color:C.textMuted },
  recipRow:    { fontSize:12, marginTop:2 },
  recipLabel:  { color:C.textMuted, fontWeight:600 },
  recipVal:    { color:C.textSecond },
  detailToggle:{
    background:'none', border:'none', color:C.blue, fontSize:11,
    cursor:'pointer', padding:'3px 0', marginTop:4,
    fontFamily:FONT, display:'flex', alignItems:'center', gap:3,
  },
  headerRight: {
    display:'flex', flexDirection:'column', alignItems:'flex-end',
    gap:8, flexShrink:0,
  },
  expandedTime:{ fontSize:11, color:C.textMuted, whiteSpace:'nowrap' },

  // Body
  bodyWrap:  { minHeight:40 },
  bodyCenter:{
    display:'flex', alignItems:'center', justifyContent:'center',
    gap:10, padding:'28px 0', fontSize:13,
  },
  retryBtn: {
    display:'flex', alignItems:'center', gap:5,
    background:'none', border:`1px solid ${C.red}`, borderRadius:4,
    color:C.red, fontSize:12, cursor:'pointer', padding:'3px 8px',
    fontFamily:FONT,
  },

  // Quoted
  quotedBtn: {
    background:C.bgAlt, border:`1px solid ${C.border}`,
    borderRadius:4, color:C.textSecond, fontSize:14,
    cursor:'pointer', padding:'1px 10px', fontFamily:FONT,
    lineHeight:1.6, transition:'background .1s',
  },

  // Attachments
  attSection: {
    padding:'8px 14px 12px', borderTop:`1px solid ${C.borderLight}`,
  },
  attLabel: {
    display:'block', fontSize:12, color:C.textSecond,
    fontWeight:600, marginBottom:8,
  },
  attGrid:  { display:'flex', flexWrap:'wrap', gap:8 },
  attChip:  {
    display:'flex', alignItems:'center', gap:6,
    padding:'5px 10px', border:`1px solid ${C.border}`, borderRadius:4,
    background:C.bgAlt, cursor:'pointer', fontSize:12, color:C.text,
    fontFamily:FONT, maxWidth:220, transition:'background .1s',
  },
  attName:  { overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
    flex:1, color:C.blue },
  attSize:  { color:C.textMuted, whiteSpace:'nowrap', flexShrink:0 },

  // Bottom action buttons (Reply / Forward inside expanded card)
  cardActions: {
    display:'flex', gap:8, padding:'10px 14px 12px',
    borderTop:`1px solid ${C.borderLight}`,
  },
  cardActionBtn: {
    display:'flex', alignItems:'center', gap:6,
    padding:'5px 14px', border:`1px solid ${C.border}`, borderRadius:4,
    background:C.bg, color:C.text, fontSize:13, cursor:'pointer',
    fontFamily:FONT, transition:'background .1s, border-color .1s',
  },

  // Image banner
  imgBanner: {
    display:'flex', alignItems:'center', gap:10,
    padding:'7px 16px', background:C.orangeBg,
    borderBottom:`1px solid #ffe58f`, fontFamily:FONT,
  },
  imgBannerBtn: {
    background:'none', border:`1px solid ${C.blue}`, borderRadius:4,
    color:C.blue, fontSize:12, cursor:'pointer', padding:'2px 10px',
    fontFamily:FONT, whiteSpace:'nowrap', flexShrink:0,
  },

  // Banner close
  bannerClose: {
    background:'none', border:'none', cursor:'pointer',
    padding:'2px 4px', borderRadius:3, color:C.textMuted,
    display:'flex', alignItems:'center',
  },

  // ── ReplyCompose ──────────────────────────────────────────────────────────
  rcRoot: {
    display:'flex', flexDirection:'column', height:'100%',
    background:C.bg, fontFamily:FONT, overflow:'hidden',
  },
  rcActionBar: {
    display:'flex', alignItems:'center', gap:12,
    padding:'8px 16px', borderBottom:`1px solid ${C.border}`,
    flexShrink:0, background:C.bg, minHeight:50,
  },
  rcSendBtn: {
    display:'inline-flex', alignItems:'center',
    height:32, padding:'0 14px',
    background:C.blue, color:'#fff',
    border:'none', borderRadius:'4px 0 0 4px',
    fontSize:13, fontWeight:600, cursor:'pointer',
    fontFamily:FONT, transition:'background .15s',
  },
  rcSendChevron: {
    display:'flex', alignItems:'center', justifyContent:'center',
    width:28, height:32, background:C.blue, color:'#fff',
    border:'none', borderRadius:'0 4px 4px 0',
    borderLeft:'1px solid rgba(255,255,255,.3)', cursor:'pointer',
  },
  rcFromLabel: {
    fontSize:13, color:C.textSecond, fontFamily:FONT,
    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
    maxWidth:340,
  },
  rcIconBtn: {
    width:30, height:30,
    display:'flex', alignItems:'center', justifyContent:'center',
    background:'transparent', border:'none', borderRadius:4,
    color:C.textMuted, cursor:'pointer', fontSize:14,
    transition:'background .12s, color .12s',
  },
  rcScrollArea: {
    flex:1, overflowY:'auto',
    display:'flex', flexDirection:'column',
  },
  rcToRow: {
    display:'flex', alignItems:'center', gap:8,
    padding:'7px 16px', borderBottom:`1px solid ${C.borderLight}`,
    minHeight:38,
  },
  rcFieldLabel: {
    fontSize:13, color:C.textMuted, fontWeight:600, minWidth:28, flexShrink:0,
  },
  rcToValue: {
    fontSize:13, color:C.text, flex:1, fontFamily:FONT,
  },
  rcToInput: {
    flex:1, border:'none', outline:'none', fontSize:13,
    color:C.text, background:'transparent', fontFamily:FONT,
  },
  rcTextarea: {
    height: 200,
    border:'none', outline:'none', resize:'none',
    padding:'14px 18px',
    fontSize:14, fontFamily:"'Calibri','Segoe UI',Arial,sans-serif",
    color:C.text, lineHeight:1.6, background:C.bg,
    overflowY: 'auto',
  },
  rcError: {
    padding:'4px 16px', fontSize:12, color:C.red,
    background:C.redBg, fontFamily:FONT,
  },
  rcDotsRow: {
    padding:'6px 16px', borderTop:`1px solid ${C.borderLight}`, flexShrink:0,
  },
  rcDotsBtn: {
    background:C.bgAlt, border:`1px solid ${C.border}`, borderRadius:4,
    color:C.textSecond, fontSize:13, cursor:'pointer',
    padding:'2px 12px', lineHeight:1.6, fontFamily:FONT,
    transition:'background .1s', display:'inline-flex', alignItems:'center',
  },
  rcQuoted: {
    borderTop:`1px solid ${C.borderLight}`,
    background:C.bg, flexShrink:0,
  },
  rcQuotedMeta: {
    padding:'12px 18px 8px', fontSize:13,
    color:C.text, fontFamily:FONT,
    display:'flex', flexDirection:'column', gap:3,
  },
  rcQuotedRow: { display:'flex', gap:6 },
  rcQuotedLabel: { fontWeight:700, minWidth:60, flexShrink:0, color:C.text },
  rcQuotedValue: { color:C.textSecond },
};

// ---------------------------------------------------------------------------
// Hover CSS
// ---------------------------------------------------------------------------

const CSS_HOVER = `
  .rp-collapsed:hover             { background:${C.borderLight} !important; }
  .rp-header-click:hover          { background:${C.bgAlt}; cursor:pointer; }
  .rp-btn:hover                   { background:${C.blueLight} !important; }
  .rp-tool-btn:hover              { background:${C.borderLight} !important; border-color:${C.textMuted} !important; }
  .rp-icon-btn:hover              { background:${C.borderLight} !important; }
  .rp-icon-btn--danger:hover      { background:${C.redBg}     !important; border-color:${C.red} !important; }
  .rp-icon-btn--danger:hover svg,
  .rp-icon-btn--danger:hover span { color:${C.red} !important; }
  .rp-expand-all:hover            { background:${C.blueLight} !important; border-radius:3px; }
  .rc-send-btn:hover:not(:disabled)    { background:${C.blueDark} !important; }
  .rc-icon-btn:hover                   { background:${C.borderLight} !important; color:${C.text} !important; }
  .rc-icon-btn--discard:hover          { color:${C.red} !important; background:${C.redBg} !important; }
  .rc-dots-btn:hover                   { background:${C.borderLight} !important; }
  .rp-card-action-btn:hover           { background:${C.bgAlt} !important; border-color:${C.textMuted} !important; }
`;
